import {
  useCallback,
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "../styles/pages/Database.module.css";
import { contactsService } from "../services/contacts.service";
import { accountsService } from "../services/accounts.service";
import { companiesService } from "../services/companies.service";
import { validationService } from "../services/validation.service";
import { useAuth } from "../contexts/AuthContext";
import { useLeadContext } from "../contexts/LeadContext";
import { toCountryOption } from "../utils/countryOptions";
import { getVerifiedStatusTooltip } from "../utils/verificationLabels";
import { describeRequestError } from "../utils/requestErrorMessage";
import {
  getSelectableAccounts,
  resolveSelectableAccountId,
} from "../utils/accountOptions";
import ContactDetailModal from "../components/database/ContactDetailModal";
import CompanyDetailModal from "../components/database/CompanyDetailModal";
import FinalScoutModal from "../components/database/FinalScoutModal";
import PersonImportModal from "../components/database/PersonImportModal";
import PersonImportResultModal from "../components/database/PersonImportResultModal";
import SearchInput from "../ui/inputs/SearchInput";
import ChoiceChip from "../ui/selectors/ChoiceChip";
import FloatingModal from "../ui/Modals/FloatingModal";
import Button from "../ui/buttons/Button";
import { ReactComponent as AddIcon } from "../assets/icons/add.svg";
import { ReactComponent as GlassIcon } from "../assets/icons/glass.svg";
import { ReactComponent as ImportIcon } from "../assets/icons/downloadIcon.svg";
import StatusBadge from "../ui/tags/StatusBadge";
import LoadingScreen from "../ui/loadingScreen/LoadingScreen";
import { ReactComponent as Vector9Icon } from "../assets/images/vector_arrow.svg";

const COMPANY_COLS = "36px 2fr 1fr 1.5fr 1.2fr 1fr";
// Grilla de contactos con columna extra de checkbox para la selección múltiple
// (la grilla base sin checkbox vive en el CSS: 1fr 1.4fr 1fr 1.6fr 1.2fr 80px).
const CONTACT_COLS = "36px 1fr 1.4fr 1fr 1.6fr 1.2fr 80px";
const PAGE_SIZE = 100;
const FILTER_PAGE_SIZE = 25;
const FILTER_SEARCH_DEBOUNCE_MS = 300;
const FILTER_SCROLL_THRESHOLD_PX = 40;
const MERGEABLE_COMPANY_FIELDS = [
  "legal_name",
  "industry",
  "website",
  "phone",
  "country",
  "comment",
];

function normalizeLegalName(legalName) {
  return (legalName || "").trim().replace(/\s+/g, " ").toLowerCase();
}
// El endpoint de verificación masiva acepta entre 1 y 500 personas.
const MAX_VERIFY_SELECTION = 500;

function formatTotalCount(total, singularLabel, pluralLabel) {
  const label = total === 1 ? singularLabel : pluralLabel;
  return `${total.toLocaleString("es-AR")} ${label}`;
}

function FilterChip({ label, value, valueLabel, onChange, loadOptions }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [hasMoreOptions, setHasMoreOptions] = useState(false);
  const chipRef = useRef(null);
  const optionsRequestRef = useRef(0);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setOptions([]);
      setHasMoreOptions(false);
      return undefined;
    }
    function onDown(event) {
      if (!chipRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const fetchOptionsPage = useCallback(
    async (query, offset) => {
      const generation = ++optionsRequestRef.current;
      setLoadingOptions(true);
      try {
        const page = await loadOptions({
          search: query,
          offset,
          limit: FILTER_PAGE_SIZE,
        });
        if (generation !== optionsRequestRef.current) return;
        setOptions((prev) =>
          offset === 0 ? page.options : [...prev, ...page.options],
        );
        setHasMoreOptions(Boolean(page.hasMore));
      } catch (error) {
        if (generation !== optionsRequestRef.current) return;
        console.error("Error cargando las opciones del filtro:", error);
        setHasMoreOptions(false);
        if (offset === 0) setOptions([]);
      } finally {
        if (generation === optionsRequestRef.current) setLoadingOptions(false);
      }
    },
    [loadOptions],
  );

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => {
      fetchOptionsPage(search.trim(), 0);
    }, FILTER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [open, search, fetchOptionsPage]);

  function handleOptionsScroll(event) {
    if (loadingOptions || !hasMoreOptions) return;
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight > FILTER_SCROLL_THRESHOLD_PX) return;
    fetchOptionsPage(search.trim(), options.length);
  }

  return (
    <div className={styles.filterChipWrap} ref={chipRef}>
      <button
        className={`${styles.filterChipBtn} ${value ? styles.filterChipBtnActive : ""}`}
        onClick={() => setOpen((isOpen) => !isOpen)}
        type="button"
      >
        <span>{valueLabel || label}</span>
        <svg
          className={`${styles.filterChevron} ${open ? styles.filterChevronOpen : ""}`}
          viewBox="0 0 14 14"
          fill="none"
        >
          <path
            d="M3 5L7 9L11 5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className={styles.filterMenu}>
          <input
            className={styles.filterSearch}
            type="text"
            placeholder="Buscar..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            autoFocus
          />

          <div className={styles.filterOptions} onScroll={handleOptionsScroll}>
            {value && (
              <button
                className={styles.filterClear}
                onClick={() => { onChange(null); setOpen(false); }}
              >
                Limpiar filtro
              </button>
            )}
            {options.map((option) => (
              <button
                key={option.value}
                className={`${styles.filterOption} ${value === option.value ? styles.filterOptionActive : ""}`}
                onClick={() => { onChange(option); setOpen(false); }}
              >
                {option.label}
              </button>
            ))}
            {loadingOptions && (
              <div className={styles.filterOption} style={{ color: "var(--text-tertiary)", cursor: "default" }}>
                Cargando...
              </div>
            )}
            {!loadingOptions && !options.length && (
              <div className={styles.filterOption} style={{ color: "var(--text-tertiary)", cursor: "default" }}>
                Sin opciones
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SortableHeaderCell({ label, field, ordering, onSort }) {
  const direction =
    ordering === field ? "asc" : ordering === `-${field}` ? "desc" : null;
  return (
    <button
      type="button"
      className={`${styles.headerCell} ${styles.headerCellSortable}`}
      onClick={() => onSort(field)}
      aria-label={`Ordenar por ${label}`}
    >
      <span className={styles.headerCellLabel}>{label}</span>
      {direction && (
        <span className={styles.sortIndicator}>
          {direction === "asc" ? "▲" : "▼"}
        </span>
      )}
    </button>
  );
}

function SelectCell({ checked, onToggle, label, disabled = false }) {
  return (
    <div
      className={styles.selectCell}
      onClick={(event) => {
        event.stopPropagation();
        if (!disabled) onToggle();
      }}
    >
      <input
        type="checkbox"
        className={styles.selectCheckbox}
        checked={checked}
        onChange={onToggle}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        disabled={disabled}
        aria-label={label}
      />
    </div>
  );
}

function ContactRow({ contact, onClick, selected, onToggleSelect }) {
  const activeEmails = contact.emails?.filter((email) => email.active !== false && email.data) ?? [];
  const primaryEmail = activeEmails[0]?.data ?? "-";
  const extraCount = activeEmails.length - 1;
  const primaryPhone = contact.contacts?.[0]?.data ?? "-";
  const fullName = `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim();

  return (
    <div className={styles.tableRow} onClick={onClick} role="button" tabIndex={0}
      onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onClick(); } }}
    >
      <div className={styles.rowBody} style={{ gridTemplateColumns: CONTACT_COLS }}>
        <SelectCell
          checked={selected}
          onToggle={() => onToggleSelect(contact.id)}
          label={`Seleccionar a ${fullName || "contacto"}`}
        />
        <div className={styles.rowCell}>{contact.company_name || "-"}</div>
        <div className={styles.rowCell}>{fullName || "-"}</div>
        <div className={styles.rowCellSecondary}>{contact.position || "-"}</div>
        <div className={styles.emailCell}>
          <span className={styles.emailText}>{primaryEmail}</span>
          {extraCount > 0 && (
            <span className={styles.emailExtraCount}>+{extraCount}</span>
          )}
        </div>
        <div className={styles.rowCellSecondary}>{primaryPhone}</div>
        <div className={styles.rowCellSecondary}>
          <StatusBadge
            isActive={!!contact.is_verified}
            size={20}
            title={getVerifiedStatusTooltip(contact)}
          />
        </div>
      </div>

      <button
        className={styles.rowActionBtn}
        onClick={(event) => { event.stopPropagation(); onClick(); }}
        aria-label="Ver detalle"
      >
        <Vector9Icon className={styles.rowActionIcon} />
      </button>
    </div>
  );
}

function CompanyRow({ company, countryName, onClick, selected, onToggleSelect }) {
  return (
    <div className={styles.tableRow} onClick={onClick} role="button" tabIndex={0}
      onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onClick(); } }}
    >
      <div className={styles.rowBody} style={{ gridTemplateColumns: COMPANY_COLS }}>
        <SelectCell
          checked={selected}
          onToggle={() => onToggleSelect(company.id)}
          label={`Seleccionar ${company.name || "empresa"}`}
        />
        <div className={styles.rowCell}>{company.name || "-"}</div>
        <div className={styles.rowCellSecondary}>{company.industry || "-"}</div>
        <div className={styles.rowCellSecondary}>{company.website || "-"}</div>
        <div className={styles.rowCellSecondary}>{company.phone || "-"}</div>
        <div className={styles.rowCellSecondary}>{countryName || "-"}</div>
      </div>

      <button
        className={styles.rowActionBtn}
        onClick={(event) => { event.stopPropagation(); onClick(); }}
        aria-label="Ver detalle"
      >
        <Vector9Icon className={styles.rowActionIcon} />
      </button>
    </div>
  );
}

export default function DatabasePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const resultImportId = searchParams.get("import");
  const { auth, activeAccountId, accounts, supervisedAccounts = [] } = useAuth();
  const { countries } = useLeadContext();
  const [tab, setTab] = useState("contactos");
  const [verifyAccountId, setVerifyAccountId] = useState(null);

  const contactsQueryRef = useRef(0);
  const allContactCountRef = useRef(null);
  const companiesQueryRef = useRef(0);
  const scrollStateRef = useRef({});
  const innerRef = useRef(null);
  const pendingScrollRef = useRef(null);

  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [loadingMoreContacts, setLoadingMoreContacts] = useState(false);
  const [search, setSearch] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [filterCompanyId, setFilterCompanyId] = useState("");
  const [filterPosition, setFilterPosition] = useState("");
  const [contactOrdering, setContactOrdering] = useState(null);
  const [companyOrdering, setCompanyOrdering] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showFinalScout, setShowFinalScout] = useState(false);
  const [showNewContact, setShowNewContact] = useState(false);
  const [showPersonImport, setShowPersonImport] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [startingVerification, setStartingVerification] = useState(false);
  const [verifyNotice, setVerifyNotice] = useState(null);
  const [firstContactNotice, setFirstContactNotice] = useState(false);
  const [disablingSharing, setDisablingSharing] = useState(false);
  const [showSharingDisableConfirm, setShowSharingDisableConfirm] = useState(false);
  const [sharingEnabled, setSharingEnabled] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [companiesTotal, setCompaniesTotal] = useState(0);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingMoreCompanies, setLoadingMoreCompanies] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showNewCompany, setShowNewCompany] = useState(false);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState(new Set());
  const [showCompanyMergeModal, setShowCompanyMergeModal] = useState(false);

  const selectedAccount = accounts.find((account) => Number(account.id) === Number(activeAccountId));
  const selectedRootAccountId = selectedAccount
    ? Number(selectedAccount.father_account?.id ?? selectedAccount.father_account ?? selectedAccount.id)
    : null;
  const selectedRootAccount = accounts.find((account) => Number(account.id) === selectedRootAccountId);
  const hasSelectedRootAccount = Boolean(selectedRootAccount);
  const selectedRootSharingEnabled = Boolean(selectedRootAccount && !selectedRootAccount.private_prospecting_data);
  const canManageSharing = supervisedAccounts.some((account) => Number(account.id) === selectedRootAccountId);
  const sharingNoticeKey = `first-contact-network-notice:${auth?.user?.id ?? "user"}:${selectedRootAccountId ?? "account"}`;

  useEffect(() => {
    setLoadingContacts(true);
    setLoadingMoreContacts(false);
    setContacts([]);
    setContactsTotal(0);
    setSelectedContactIds(new Set());
    const gen = ++contactsQueryRef.current;
    const params = { limit: PAGE_SIZE, offset: 0 };
    if (search.trim()) params.search = search.trim();
    if (filterCompanyId) params.company = filterCompanyId;
    if (filterPosition) params.position = filterPosition;
    if (contactOrdering) params.ordering = contactOrdering;
    const timer = setTimeout(() => {
      contactsService
        .list(params)
        .then(({ results, count }) => {
          if (gen !== contactsQueryRef.current) return;
          setContacts(results ?? []);
          setContactsTotal(count);
        })
        .catch((err) => { if (gen === contactsQueryRef.current) console.error(err); })
        .finally(() => { if (gen === contactsQueryRef.current) setLoadingContacts(false); });
    }, 400);
    return () => clearTimeout(timer);
  }, [search, filterCompanyId, filterPosition, contactOrdering]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSharingEnabled(selectedRootSharingEnabled);
  }, [selectedRootAccount?.id, selectedRootSharingEnabled]);

  useEffect(() => {
    allContactCountRef.current = null;
    if (!activeAccountId || !hasSelectedRootAccount) return undefined;
    let active = true;
    contactsService.list({ limit: 1, offset: 0 })
      .then(({ count }) => {
        if (!active) return;
        allContactCountRef.current = count;
        if (count === 0 && sharingEnabled && !localStorage.getItem(sharingNoticeKey)) {
          setFirstContactNotice(true);
        } else if (count > 0) {
          localStorage.setItem(sharingNoticeKey, "seen");
          setFirstContactNotice(false);
        }
      })
      .catch(() => { });
    return () => {
      active = false;
    };
  }, [activeAccountId, hasSelectedRootAccount, selectedRootAccount?.id, sharingEnabled, sharingNoticeKey]);

  async function refreshAllContactCount() {
    try {
      const { count } = await contactsService.list({ limit: 1, offset: 0 });
      allContactCountRef.current = count;
      if (count === 0 && sharingEnabled && !localStorage.getItem(sharingNoticeKey)) {
        setFirstContactNotice(true);
      } else if (count > 0) {
        localStorage.setItem(sharingNoticeKey, "seen");
        setFirstContactNotice(false);
      }
    } catch { }
  }

  async function handleDisableSharingFromNotice() {
    if (!selectedRootAccount || !canManageSharing || disablingSharing) return;
    setShowSharingDisableConfirm(true);
  }

  async function confirmDisableSharing() {
    if (!selectedRootAccount || !canManageSharing || disablingSharing) return;
    setDisablingSharing(true);
    try {
      await accountsService.update(selectedRootAccount.id, { private_prospecting_data: true });
      setSharingEnabled(false);
      setFirstContactNotice(false);
      setShowSharingDisableConfirm(false);
      localStorage.setItem(sharingNoticeKey, "seen");
    } catch (error) {
      setVerifyNotice({
        kind: "error",
        text: error?.response?.data?.detail ?? "No se pudo desactivar el uso compartido.",
      });
    } finally {
      setDisablingSharing(false);
    }
  }

  useEffect(() => {
    if (tab !== "empresas") return;
    setLoadingCompanies(true);
    setLoadingMoreCompanies(false);
    setCompanies([]);
    setCompaniesTotal(0);
    setSelectedCompanyIds(new Set());
    const gen = ++companiesQueryRef.current;
    const params = { limit: PAGE_SIZE, offset: 0 };
    if (companySearch.trim()) params.search = companySearch.trim();
    if (companyOrdering) params.ordering = companyOrdering;
    const timer = setTimeout(() => {
      companiesService
        .list(params)
        .then(({ results, count }) => {
          if (gen !== companiesQueryRef.current) return;
          setCompanies(results ?? []);
          setCompaniesTotal(count);
        })
        .catch((err) => { if (gen === companiesQueryRef.current) console.error(err); })
        .finally(() => { if (gen === companiesQueryRef.current) setLoadingCompanies(false); });
    }, 400);
    return () => clearTimeout(timer);
  }, [companySearch, tab, companyOrdering]); // eslint-disable-line react-hooks/exhaustive-deps

  function switchTab(next) {
    setTab(next);
    setSearch("");
    setCompanySearch("");
  }

  function handleFilterCompanyChange(option) {
    setFilterCompany(option?.label ?? "");
    setFilterCompanyId(option?.value ?? "");
  }

  function handleFilterPositionChange(option) {
    setFilterPosition(option?.value ?? "");
  }

  const loadCompanyOptions = useCallback(
    async ({ search: query, offset, limit }) => {
      const { results, count } = await companiesService.list({
        ...(query ? { search: query } : {}),
        limit,
        offset,
      });
      return {
        options: results.map((company) => ({
          value: String(company.id),
          label: company.name,
        })),
        hasMore: offset + results.length < count,
      };
    },
    [],
  );

  const loadPositionOptions = useCallback(
    async ({ search: query, offset, limit }) => {
      const { results, count } = await contactsService.positions({
        ...(query ? { search: query } : {}),
        limit,
        offset,
      });
      return {
        options: results.map((position) => ({ value: position, label: position })),
        hasMore: offset + results.length < count,
      };
    },
    [],
  );

  function cycleOrdering(currentOrdering, field) {
    if (currentOrdering === field) return `-${field}`;
    if (currentOrdering === `-${field}`) return null;
    return field;
  }

  function handleContactSort(field) {
    setContactOrdering((prev) => cycleOrdering(prev, field));
  }

  function handleCompanySort(field) {
    setCompanyOrdering((prev) => cycleOrdering(prev, field));
  }

  const countryNamesById = useMemo(
    () =>
      Object.fromEntries(
        (countries ?? [])
          .filter((country) => country?.id != null)
          .map((country) => [country.id, toCountryOption(country)?.name ?? ""]),
      ),
    [countries],
  );

  const accountOptions = useMemo(
    () => getSelectableAccounts(accounts),
    [accounts]
  );

  const creditsAccountId =
    verifyAccountId ??
    resolveSelectableAccountId(accounts, activeAccountId) ??
    activeAccountId ??
    accountOptions[0]?.id ??
    null;

  const filtered = contacts;

  const contactsHasMore = contacts.length < contactsTotal;
  const companiesHasMore = companies.length < companiesTotal;

  const selectedContacts = useMemo(
    () => contacts.filter((contact) => selectedContactIds.has(contact.id)),
    [contacts, selectedContactIds]
  );

  const mergeSharedEmail = useMemo(() => {
    if (selectedContacts.length < 2) return null;
    const emailSets = selectedContacts.map(
      (contact) =>
        new Set(
          (contact.emails ?? [])
            .filter((email) => email.active !== false && email.data)
            .map((email) => email.data.trim().toLowerCase())
        )
    );
    const [firstEmailSet, ...restEmailSets] = emailSets;
    return (
      [...firstEmailSet].find((address) =>
        restEmailSets.every((emailSet) => emailSet.has(address))
      ) ?? null
    );
  }, [selectedContacts]);

  const mergePreviewContact = useMemo(() => {
    if (selectedContacts.length < 2) return null;
    const orderedContacts = [...selectedContacts].sort(
      (a, b) => new Date(a.created) - new Date(b.created) || a.id - b.id
    );
    const [survivor, ...duplicates] = orderedContacts;

    const emails = (survivor.emails ?? []).filter(
      (email) => email.active !== false && email.data
    );
    const usedEmailTypes = new Set(
      emails
        .filter((email) => email.type != null)
        .map((email) => email.type)
    );
    const phones = (survivor.contacts ?? []).filter((phone) => phone.data);
    const usedPhoneTypes = new Set(
      phones
        .filter((phone) => phone.type != null)
        .map((phone) => phone.type)
    );

    for (const duplicate of duplicates) {
      for (const email of duplicate.emails ?? []) {
        if (email.active === false || !email.data) continue;
        if (email.type != null && usedEmailTypes.has(email.type)) continue;
        emails.push(email);
        if (email.type != null) usedEmailTypes.add(email.type);
      }
      for (const phone of duplicate.contacts ?? []) {
        if (!phone.data) continue;
        if (phone.type != null && usedPhoneTypes.has(phone.type)) continue;
        phones.push(phone);
        if (phone.type != null) usedPhoneTypes.add(phone.type);
      }
    }

    const merged = { ...survivor, emails, contacts: phones };
    if (!merged.company) {
      const duplicateWithCompany = duplicates.find(
        (duplicate) => duplicate.company
      );
      if (duplicateWithCompany) {
        merged.company = duplicateWithCompany.company;
        merged.company_name = duplicateWithCompany.company_name;
      }
    }
    if (!merged.position) {
      merged.position =
        duplicates.find((duplicate) => duplicate.position)?.position ?? "";
    }

    return merged;
  }, [selectedContacts]);

  function handleContactsMerged() {
    setShowMergeModal(false);
    setSelectedContactIds(new Set());
    setVerifyNotice({
      kind: "info",
      text: "Contactos unificados correctamente.",
    });
    handleContactSaved(null);
  }

  const selectedCompanies = useMemo(
    () => companies.filter((company) => selectedCompanyIds.has(company.id)),
    [companies, selectedCompanyIds],
  );

  const mergeSharedLegalName = useMemo(() => {
    if (selectedCompanies.length < 2) return null;
    const legalNames = new Set(
      selectedCompanies.map((company) => normalizeLegalName(company.legal_name)),
    );
    if (legalNames.size !== 1) return null;
    const [sharedLegalName] = legalNames;
    if (!sharedLegalName) return null;
    return selectedCompanies[0].legal_name.trim();
  }, [selectedCompanies]);

  const mergePreviewCompany = useMemo(() => {
    if (selectedCompanies.length < 2) return null;
    const orderedCompanies = [...selectedCompanies].sort(
      (a, b) => new Date(a.created) - new Date(b.created) || a.id - b.id,
    );
    const [survivor, ...duplicates] = orderedCompanies;
    const preview = { ...survivor };
    for (const field of MERGEABLE_COMPANY_FIELDS) {
      if (preview[field]) continue;
      const duplicateValue = duplicates.find((duplicate) => duplicate[field])?.[field];
      if (duplicateValue) preview[field] = duplicateValue;
    }
    return preview;
  }, [selectedCompanies]);

  const allCompaniesSelected =
    companies.length > 0 &&
    companies.every((company) => selectedCompanyIds.has(company.id));

  function toggleCompanySelection(companyId) {
    setSelectedCompanyIds((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  }

  function toggleSelectAllCompanies() {
    if (allCompaniesSelected) {
      setSelectedCompanyIds(new Set());
    } else {
      setSelectedCompanyIds(new Set(companies.map((company) => company.id)));
    }
  }

  async function handleOpenPersonFromCompany(personId) {
    try {
      const person = await contactsService.getDetail(personId);
      setSelectedCompany(null);
      setSelectedContact(person);
    } catch (error) {
      console.error("Error abriendo la persona de la empresa:", error);
    }
  }

  function handleCompaniesMerged() {
    setShowCompanyMergeModal(false);
    setSelectedCompanyIds(new Set());
    setSelectedCompany(null);
    handleCompanySaved(null);
  }

  const allFilteredSelected =
    filtered.length > 0 &&
    filtered.every((contact) => selectedContactIds.has(contact.id));

  function toggleContactSelection(contactId) {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  }

  function toggleSelectAllContacts() {
    if (allFilteredSelected) {
      setSelectedContactIds(new Set());
    } else {
      setSelectedContactIds(new Set(filtered.map((contact) => contact.id)));
    }
  }

  async function handleVerifySelected() {
    const personIds = [...selectedContactIds];
    if (!personIds.length || startingVerification) return;
    if (personIds.length > MAX_VERIFY_SELECTION) {
      setVerifyNotice({
        kind: "error",
        text: `Podés verificar hasta ${MAX_VERIFY_SELECTION} contactos por vez.`,
      });
      return;
    }
    setStartingVerification(true);
    setVerifyNotice(null);
    try {
      const data = await validationService.verifyBulk({
        accountId: creditsAccountId,
        personIds,
      });
      if (data.task_id) {
        navigate(`/database/verification/${data.task_id}`);
        return;
      }
      // Sin task: todos los emails se resolvieron al instante.
      const reusedCount = data.reused?.length ?? 0;
      const skippedCount = data.skipped_persons?.length ?? 0;
      const noticeParts = [
        reusedCount === 1
          ? "Se verificó 1 email (1 crédito consumido)."
          : `Se verificaron ${reusedCount} emails (${reusedCount} créditos consumidos).`,
      ];
      if (skippedCount) {
        noticeParts.push(
          skippedCount === 1
            ? "1 contacto se salteó por no tener email activo."
            : `${skippedCount} contactos se saltearon por no tener email activo.`,
        );
      }
      setVerifyNotice({ kind: "info", text: noticeParts.join(" ") });
      setSelectedContactIds(new Set());
      handleContactSaved(null);
    } catch (err) {
      const statusCode = err?.response?.status;
      if (statusCode === 402) {
        setVerifyNotice({
          kind: "error",
          text: "Sin créditos de validación suficientes. Podés recargar desde Configuración → Créditos.",
        });
      } else if (statusCode === 400) {
        setVerifyNotice({
          kind: "error",
          text: err.response?.data?.detail ?? "No se pudo iniciar la verificación.",
        });
      } else if (statusCode === 403) {
        setVerifyNotice({
          kind: "error",
          text: describeRequestError(err, "No se pudo iniciar la verificación"),
        });
      } else if (statusCode === 502) {
        setVerifyNotice({
          kind: "error",
          text: "Error del verificador. Intentá de nuevo.",
        });
      } else if (statusCode === 504) {
        setVerifyNotice({
          kind: "error",
          text:
            err.response?.data?.detail ??
            "El verificador no respondió a tiempo. Revisá en unos minutos antes de reintentar.",
        });
      } else {
        setVerifyNotice({
          kind: "error",
          text: "Ocurrió un error inesperado. Intentá de nuevo.",
        });
      }
    } finally {
      setStartingVerification(false);
    }
  }

  useLayoutEffect(() => {
    const p = pendingScrollRef.current;
    if (!p) return;
    pendingScrollRef.current = null;
    if (innerRef.current) innerRef.current.scrollTop = p.inner;
    window.scrollTo({ top: p.win, behavior: "instant" });
  });

  async function loadMoreContacts() {
    const gen = contactsQueryRef.current;
    setLoadingMoreContacts(true);
    const params = { limit: PAGE_SIZE, offset: contacts.length };
    if (search.trim()) params.search = search.trim();
    if (filterCompanyId) params.company = filterCompanyId;
    if (filterPosition) params.position = filterPosition;
    if (contactOrdering) params.ordering = contactOrdering;
    try {
      const { results, count } = await contactsService.list(params);
      if (gen !== contactsQueryRef.current) return;
      pendingScrollRef.current = { inner: innerRef.current?.scrollTop ?? 0, win: window.scrollY };
      setContacts((prev) => [...prev, ...(results ?? [])]);
      setContactsTotal(count);
    } catch (err) {
      if (gen === contactsQueryRef.current) console.error(err);
    } finally {
      if (gen === contactsQueryRef.current) setLoadingMoreContacts(false);
    }
  }

  async function loadMoreCompanies() {
    const gen = companiesQueryRef.current;
    setLoadingMoreCompanies(true);
    const params = { limit: PAGE_SIZE, offset: companies.length };
    if (companySearch.trim()) params.search = companySearch.trim();
    if (companyOrdering) params.ordering = companyOrdering;
    try {
      const { results, count } = await companiesService.list(params);
      if (gen !== companiesQueryRef.current) return;
      pendingScrollRef.current = { inner: innerRef.current?.scrollTop ?? 0, win: window.scrollY };
      setCompanies((prev) => [...prev, ...(results ?? [])]);
      setCompaniesTotal(count);
    } catch (err) {
      if (gen === companiesQueryRef.current) console.error(err);
    } finally {
      if (gen === companiesQueryRef.current) setLoadingMoreCompanies(false);
    }
  }

  scrollStateRef.current = {
    tab,
    canLoadContacts: contactsHasMore && !loadingMoreContacts && !loadingContacts,
    canLoadCompanies: companiesHasMore && !loadingMoreCompanies && !loadingCompanies,
    loadMoreContacts,
    loadMoreCompanies,
  };

  useEffect(() => {
    const check = (scrollEl) => {
      if (!scrollEl) return;
      if (scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight > 300) return;
      const s = scrollStateRef.current;
      if (s.tab === "contactos" && s.canLoadContacts) s.loadMoreContacts();
      else if (s.tab === "empresas" && s.canLoadCompanies) s.loadMoreCompanies();
    };
    const onInnerScroll = () => check(innerRef.current);
    const onWindowScroll = () => check(document.documentElement);
    const el = innerRef.current;
    el?.addEventListener("scroll", onInnerScroll, { passive: true });
    window.addEventListener("scroll", onWindowScroll, { passive: true });
    return () => {
      el?.removeEventListener("scroll", onInnerScroll);
      window.removeEventListener("scroll", onWindowScroll);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function buildContactListParams(extra = {}) {
    const params = { limit: PAGE_SIZE, offset: 0, ...extra };
    if (search.trim()) params.search = search.trim();
    if (filterCompanyId) params.company = filterCompanyId;
    if (filterPosition) params.position = filterPosition;
    if (contactOrdering) params.ordering = contactOrdering;
    return params;
  }

  function matchesActiveContactFilters(contact) {
    if (filterCompanyId && String(contact.company ?? "") !== String(filterCompanyId)) {
      return false;
    }
    if (filterPosition && contact.position !== filterPosition) return false;
    return true;
  }

  function handleCloseImportResult() {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("import");
    setSearchParams(nextSearchParams, { replace: true });
  }

  function handleImportResultLoaded(importData) {
    if (importData.status !== "finished" && importData.status !== "failed") return;
    handleContactSaved(null);
    handleCompanySaved(null);
    refreshAllContactCount();
  }

  function handleContactSaved(saved) {
    if (!saved) {
      contactsService
        .list(buildContactListParams())
        .then(({ results, count }) => {
          setContacts(results ?? []);
          setContactsTotal(count);
        })
        .catch(() => { });
      return;
    }
    const isNew = !contacts.find((c) => c.id === saved.id);
    if (isNew) refreshAllContactCount();
    if (matchesActiveContactFilters(saved)) {
      setContacts((prev) => {
        const existingIndex = prev.findIndex((c) => c.id === saved.id);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = saved;
          return updated;
        }
        return [saved, ...prev];
      });
      if (isNew) setContactsTotal((t) => t + 1);
    } else {
      setContacts((prev) => prev.filter((c) => c.id !== saved.id));
      if (!isNew) setContactsTotal((t) => Math.max(0, t - 1));
    }
    if (showNewContact) {
      setShowNewContact(false);
    } else {
      setSelectedContact(saved);
    }
  }

  function handleContactDeleted(id) {
    setContacts((prev) => prev.filter((c) => c.id !== id));
    setContactsTotal((prev) => Math.max(0, prev - 1));
    setSelectedContact(null);
  }

  function handleCompanySaved(saved) {
    if (!saved) {
      const params = { limit: PAGE_SIZE, offset: 0 };
      if (companySearch.trim()) params.search = companySearch.trim();
      if (companyOrdering) params.ordering = companyOrdering;
      companiesService
        .list(params)
        .then(({ results, count }) => {
          setCompanies(results ?? []);
          setCompaniesTotal(count);
        })
        .catch(() => { });
      return;
    }
    const isNew = !companies.find((c) => c.id === saved.id);
    setCompanies((prev) => {
      const existingIndex = prev.findIndex((c) => c.id === saved.id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = saved;
        return updated;
      }
      return [saved, ...prev];
    });
    if (isNew) setCompaniesTotal((t) => t + 1);
    if (showNewCompany) {
      setShowNewCompany(false);
    } else {
      setSelectedCompany(saved);
    }
  }

  function handleCompanyDeleted(id) {
    setCompanies((prev) => prev.filter((c) => c.id !== id));
    setCompaniesTotal((prev) => Math.max(0, prev - 1));
    setSelectedCompany(null);
  }

  return (
    <div className={styles.database}>
      <div className={styles.inner} ref={innerRef}>
        <div className={styles.content}>
          <div className={styles.pageHeader}>
            <div className={styles.pageTitle}>Database</div>
            {tab === "contactos" && !loadingContacts && (
              <span className={styles.pageCount} data-testid="database-total-count">
                {formatTotalCount(contactsTotal, "contacto", "contactos")}
              </span>
            )}
            {tab === "empresas" && !loadingCompanies && (
              <span className={styles.pageCount} data-testid="database-total-count">
                {formatTotalCount(companiesTotal, "empresa", "empresas")}
              </span>
            )}
          </div>

          <div className={styles.toolbar}>
            <div className={styles.toolbarLeft}>
              <SearchInput
                value={tab === "contactos" ? search : companySearch}
                onChange={tab === "contactos" ? setSearch : setCompanySearch}
                placeholder={
                  tab === "contactos"
                    ? "Nombre, empresa, email o teléfono…"
                    : "Buscar empresa…"
                }
                width={220}
                height={40}
              />
              <div className={styles.tabPill}>
                <ChoiceChip
                  label="Contactos"
                  selected={tab === "contactos"}
                  onClick={() => switchTab("contactos")}
                  height={32}
                  iconSize={0}
                  fontWeight={500}
                  selectedColor="var(--text-cta-primary)"
                  unselectedColor="var(--text-tertiary)"
                  selectedBackground="var(--surface-accent-hover)"
                  unselectedBackground="transparent"
                />
                <ChoiceChip
                  label="Empresas"
                  selected={tab === "empresas"}
                  onClick={() => switchTab("empresas")}
                  height={32}
                  iconSize={0}
                  fontWeight={500}
                  selectedColor="var(--text-cta-primary)"
                  unselectedColor="var(--text-tertiary)"
                  selectedBackground="var(--surface-accent-hover)"
                  unselectedBackground="transparent"
                />
              </div>
            </div>

            <div className={styles.toolbarRight}>
              {tab === "contactos" ? (
                <>
                  <FilterChip
                    label="Empresa"
                    value={filterCompanyId}
                    valueLabel={filterCompany}
                    onChange={handleFilterCompanyChange}
                    loadOptions={loadCompanyOptions}
                  />
                  <FilterChip
                    label="Posición"
                    value={filterPosition}
                    valueLabel={filterPosition}
                    onChange={handleFilterPositionChange}
                    loadOptions={loadPositionOptions}
                  />
                  {selectedContactIds.size > 0 && accountOptions.length > 1 && (
                    <select
                      className={styles.verifyAccountSelect}
                      value={creditsAccountId ?? ""}
                      onChange={(event) =>
                        setVerifyAccountId(Number(event.target.value))
                      }
                      aria-label="Cuenta que consume los créditos de verificación"
                      title="Cuenta que consume los créditos de verificación"
                    >
                      {accountOptions.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {selectedContactIds.size > 0 && (
                    <button
                      className={styles.finalScoutBtn}
                      onClick={handleVerifySelected}
                      disabled={startingVerification}
                      type="button"
                    >
                      {startingVerification
                        ? "Iniciando verificación…"
                        : `Verificar emails (${selectedContactIds.size})`}
                    </button>
                  )}
                  <button
                    className={styles.finalScoutBtn}
                    onClick={() => setShowNewContact(true)}
                    type="button"
                  >
                    <AddIcon style={{ width: 14, height: 14 }} />
                    Nuevo contacto
                  </button>
                  <button
                    className={styles.finalScoutBtn}
                    onClick={() => setShowPersonImport(true)}
                    type="button"
                  >
                    <ImportIcon className={styles.importBtnIcon} />
                    Importar contactos
                  </button>
                  <button
                    className={styles.finalScoutBtn}
                    onClick={() => setShowFinalScout(true)}
                    type="button"
                  >
                    <GlassIcon style={{ width: 14, height: 14 }} />
                    Buscar emails
                  </button>
                </>
              ) : (
                <button
                  className={styles.finalScoutBtn}
                  onClick={() => setShowNewCompany(true)}
                  type="button"
                >
                  <AddIcon style={{ width: 14, height: 14 }} />
                  Nueva empresa
                </button>
              )}
            </div>
          </div>

          {verifyNotice && tab === "contactos" && (
            <div
              className={
                verifyNotice.kind === "error"
                  ? styles.verifyNoticeError
                  : styles.verifyNoticeInfo
              }
            >
              <span>{verifyNotice.text}</span>
              <button
                type="button"
                className={styles.verifyNoticeClose}
                onClick={() => setVerifyNotice(null)}
                aria-label="Cerrar aviso"
              >
                ×
              </button>
            </div>
          )}

          {firstContactNotice && tab === "contactos" && (
            <div className={styles.firstContactNotice} role="status">
              <span>Tus contactos se suman a la red de contactos verificados. Si no querés compartirlos, desactivalo ahora y no se comparte nada.</span>
              {canManageSharing ? (
                <button type="button" onClick={handleDisableSharingFromNotice} disabled={disablingSharing}>
                  {disablingSharing ? "Desactivando..." : "Desactivar compartir"}
                </button>
              ) : (
                <span className={styles.sharingAdminHint}>Solo el administrador de la cuenta puede cambiar esta opción.</span>
              )}
              <button
                type="button"
                className={styles.firstContactNoticeClose}
                onClick={() => {
                  setFirstContactNotice(false);
                  localStorage.setItem(sharingNoticeKey, "seen");
                }}
                aria-label="Cerrar aviso"
              >
                ×
              </button>
            </div>
          )}

          {showSharingDisableConfirm && (
            <div className={styles.sharingConfirmBackdrop}>
              <section className={styles.sharingConfirmDialog} role="dialog" aria-modal="true" aria-labelledby="sharing-confirm-title">
                <h2 id="sharing-confirm-title">¿Desactivar compartir contactos?</h2>
                <p>Si confirmás ahora, el primer contacto y los siguientes no se sumarán a la red de contactos verificados.</p>
                <div className={styles.sharingConfirmActions}>
                  <button type="button" onClick={() => setShowSharingDisableConfirm(false)} disabled={disablingSharing}>
                    Seguir compartiendo
                  </button>
                  <button type="button" onClick={confirmDisableSharing} disabled={disablingSharing}>
                    {disablingSharing ? "Desactivando..." : "Desactivar"}
                  </button>
                </div>
              </section>
            </div>
          )}

          <div className={styles.tableContainer}>
            <div className={styles.table}>
              {tab === "contactos" ? (
                <>
                  <div className={styles.tableHeader}>
                    <div
                      className={styles.tableHeaderBody}
                      style={{ gridTemplateColumns: CONTACT_COLS }}
                    >
                      <SelectCell
                        checked={allFilteredSelected}
                        onToggle={toggleSelectAllContacts}
                        disabled={!filtered.length}
                        label="Seleccionar todos los contactos"
                      />
                      <SortableHeaderCell label="Empresa" field="company" ordering={contactOrdering} onSort={handleContactSort} />
                      <SortableHeaderCell label="Nombre y Apellido" field="name" ordering={contactOrdering} onSort={handleContactSort} />
                      <SortableHeaderCell label="Posición" field="position" ordering={contactOrdering} onSort={handleContactSort} />
                      <SortableHeaderCell label="Email" field="email" ordering={contactOrdering} onSort={handleContactSort} />
                      <SortableHeaderCell label="Teléfono" field="phone" ordering={contactOrdering} onSort={handleContactSort} />
                      <SortableHeaderCell label="Estado" field="verified" ordering={contactOrdering} onSort={handleContactSort} />
                    </div>
                    <div className={styles.tableHeaderSpacer} />
                  </div>

                  {loadingContacts && (
                    <LoadingScreen
                      embedded
                      mainText="Cargando contactos..."
                      subText=""
                    />
                  )}

                  {!loadingContacts && !contacts.length && (
                    <div className={styles.emptyState}>
                      <div className={styles.emptyTitle}>
                        {search.trim() || filterCompany || filterPosition
                          ? "No hay resultados para la búsqueda"
                          : "No hay contactos aún"}
                      </div>
                      <div className={styles.emptyDesc}>
                        {search.trim() || filterCompany || filterPosition
                          ? "Probá con otros filtros o términos de búsqueda."
                          : "Agregá un contacto manualmente o usá el verificador para encontrar emails profesionales."}
                      </div>
                    </div>
                  )}

                  {!loadingContacts &&
                    filtered.map((contact) => (
                      <ContactRow
                        key={contact.id}
                        contact={contact}
                        onClick={() => setSelectedContact(contact)}
                        selected={selectedContactIds.has(contact.id)}
                        onToggleSelect={toggleContactSelection}
                      />
                    ))}

                  {!loadingContacts && contacts.length > 0 && (
                    <div className={styles.tableFooter}>
                      <span className={styles.tableCount}>
                        {contacts.length} de {contactsTotal} contactos
                      </span>
                      {loadingMoreContacts && (
                        <span className={styles.loadingMoreText}>
                          <div className={styles.spinnerSm} />
                          Cargando más…
                        </span>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className={styles.tableHeader}>
                    <div
                      className={styles.tableHeaderBody}
                      style={{ gridTemplateColumns: COMPANY_COLS }}
                    >
                      <SelectCell
                        checked={allCompaniesSelected}
                        onToggle={toggleSelectAllCompanies}
                        label="Seleccionar todas las empresas"
                      />
                      <SortableHeaderCell label="Nombre" field="name" ordering={companyOrdering} onSort={handleCompanySort} />
                      <SortableHeaderCell label="Industria" field="industry" ordering={companyOrdering} onSort={handleCompanySort} />
                      <SortableHeaderCell label="Sitio web" field="website" ordering={companyOrdering} onSort={handleCompanySort} />
                      <SortableHeaderCell label="Teléfono" field="phone" ordering={companyOrdering} onSort={handleCompanySort} />
                      <div className={styles.headerCell}>País</div>
                    </div>
                    <div className={styles.tableHeaderSpacer} />
                  </div>

                  {loadingCompanies && (
                    <LoadingScreen
                      embedded
                      mainText="Cargando empresas..."
                      subText=""
                    />
                  )}

                  {!loadingCompanies && !companies.length && (
                    <div className={styles.emptyState}>
                      <div className={styles.emptyTitle}>
                        {companySearch.trim()
                          ? "No hay resultados para la búsqueda"
                          : "No hay empresas aún"}
                      </div>
                      <div className={styles.emptyDesc}>
                        {companySearch.trim()
                          ? "Probá con otros términos de búsqueda."
                          : "Agregá una empresa manualmente."}
                      </div>
                    </div>
                  )}

                  {!loadingCompanies &&
                    companies.map((company) => (
                      <CompanyRow
                        key={company.id}
                        company={company}
                        countryName={countryNamesById[company.country] ?? ""}
                        selected={selectedCompanyIds.has(company.id)}
                        onToggleSelect={toggleCompanySelection}
                        onClick={() => setSelectedCompany(company)}
                      />
                    ))}

                  {!loadingCompanies && companies.length > 0 && (
                    <div className={styles.tableFooter}>
                      <span className={styles.tableCount}>
                        {companies.length} de {companiesTotal} empresas
                      </span>
                      {loadingMoreCompanies && (
                        <span className={styles.loadingMoreText}>
                          <div className={styles.spinnerSm} />
                          Cargando más…
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedContact && (
        <ContactDetailModal
          contact={selectedContact}
          onClose={() => setSelectedContact(null)}
          onSaved={handleContactSaved}
          onDeleted={handleContactDeleted}
          onOpenLead={(leadId) => navigate(`/leads/${leadId}`)}
        />
      )}

      {showNewContact && (
        <ContactDetailModal
          contact={null}
          createMode
          onClose={() => setShowNewContact(false)}
          onSaved={handleContactSaved}
        />
      )}

      {selectedCompany && (
        <CompanyDetailModal
          company={selectedCompany}
          onClose={() => setSelectedCompany(null)}
          onSaved={handleCompanySaved}
          onDeleted={handleCompanyDeleted}
          onOpenPerson={handleOpenPersonFromCompany}
        />
      )}

      {showNewCompany && (
        <CompanyDetailModal
          company={null}
          createMode
          onClose={() => setShowNewCompany(false)}
          onSaved={handleCompanySaved}
        />
      )}

      {showPersonImport && (
        <PersonImportModal onClose={() => setShowPersonImport(false)} />
      )}

      {resultImportId && (
        <PersonImportResultModal
          importId={resultImportId}
          onClose={handleCloseImportResult}
          onLoaded={handleImportResultLoaded}
        />
      )}

      {showFinalScout && (
        <FinalScoutModal
          onClose={() => setShowFinalScout(false)}
          onContactSaved={handleContactSaved}
        />
      )}

      {tab === "contactos" &&
        !showMergeModal &&
        selectedContactIds.size >= 2 &&
        mergeSharedEmail && (
          <FloatingModal bottom={56}>
            <span className={styles.mergeFloatingLine}>
              Unificarás {selectedContactIds.size} contactos con el email{" "}
              <span className={styles.mergeFloatingEmail}>
                {mergeSharedEmail}
              </span>
            </span>
            <Button
              variant="special"
              tone="green"
              width={200}
              height={56}
              onClick={() => setShowMergeModal(true)}
            >
              Unificar contactos
            </Button>
          </FloatingModal>
        )}

      {tab === "empresas" &&
        !showCompanyMergeModal &&
        selectedCompanies.length >= 2 &&
        mergeSharedLegalName && (
          <FloatingModal bottom={56}>
            <span className={styles.mergeFloatingLine}>
              Unificarás {selectedCompanies.length} empresas con el nombre legal{" "}
              <span className={styles.mergeFloatingEmail}>
                {mergeSharedLegalName}
              </span>
            </span>
            <Button
              variant="special"
              tone="green"
              width={200}
              height={56}
              onClick={() => setShowCompanyMergeModal(true)}
            >
              Unificar empresas
            </Button>
          </FloatingModal>
        )}

      {showCompanyMergeModal && mergePreviewCompany && (
        <CompanyDetailModal
          company={mergePreviewCompany}
          mergeMode
          mergeCompanyIds={selectedCompanies.map((company) => company.id)}
          onClose={() => setShowCompanyMergeModal(false)}
          onMerged={handleCompaniesMerged}
        />
      )}

      {showMergeModal && mergePreviewContact && (
        <ContactDetailModal
          contact={mergePreviewContact}
          mergeMode
          mergePersonIds={selectedContacts.map((contact) => contact.id)}
          onClose={() => setShowMergeModal(false)}
          onMerged={handleContactsMerged}
        />
      )}
    </div>
  );
}
